import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { DraftingSourceEnum } from "@app_schema";

import { Button } from "@styled/button";
import { CompactTextArea } from "@styled/compact_text_area";
import { ErrorLine } from "@styled/error_line";
import { FloatingPanel } from "@styled/floating_panel";
import { Form } from "@styled/form";
import { Input } from "@styled/input";
import { Label } from "@styled/label";
import { SectionEditorLayout } from "@styled/section_editor_layout";
import { SourceChecklist } from "@styled/source_checklist";

import {
  SourcesFooter,
  SupportingDocumentSelect,
  sourceOptions,
  toggled,
} from "./drafting_sources";
import {
  type DraftingSections,
  useDraftingSectionsOf,
} from "./use_drafting_sections";

type Section = DraftingSections["sections"][number];
type Fields = {
  name: string;
  instructions: string;
  sources: DraftingSourceEnum[];
  supportingDocumentId: string | null;
};

// The section editor, fixed at `top`/`left`: a new section `before` the gap he
// pointed at with Insert here (null: after his last one), or a `section` from
// its ··· menu.
export const DraftingSectionEditor: FC<{
  target: { before: number | null } | { section: Section };
  top: number;
  left: number;
  onClose(): void;
}> = ({ target, top, left, onClose }) => {
  const drafting = useDraftingSectionsOf();
  const section = "section" in target ? target.section : undefined;
  const form = useForm<Fields>({
    defaultValues: {
      name: section?.name ?? "",
      instructions: section?.instructions ?? "",
      sources: section ? [...section.sources] : [],
      supportingDocumentId: section?.supportingDocumentId ?? null,
    },
  });
  const [name, sources, supportingDocumentId] = form.watch([
    "name",
    "sources",
    "supportingDocumentId",
  ]);
  const title = section ? section.name : "New section";

  const save = async ({ name, instructions, ...rest }: Fields) => {
    const fields = {
      name: name.trim(),
      instructions: instructions.trim(),
      ...rest,
    };
    const saved =
      "section" in target
        ? await drafting.updateSection(target.section.id, fields)
        : await drafting.addSection({ before: target.before, ...fields });
    if (saved) onClose();
  };
  const error = section ? drafting.error : drafting.addError;

  return (
    <FloatingPanel top={top} left={left} label={title} onClose={onClose}>
      <FormProvider {...form}>
        <Form onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <SectionEditorLayout
            title={title}
            name={
              <>
                <Label htmlFor="section-name">Name</Label>
                <Input
                  id="section-name"
                  variant="sm"
                  autoFocus
                  {...form.register("name")}
                />
              </>
            }
            instructions={
              <>
                <Label htmlFor="section-instructions">
                  What to draft · optional, used on every case
                </Label>
                <CompactTextArea
                  id="section-instructions"
                  size="line"
                  placeholder="e.g. Current restrictions and whether the claimant can return to usual work."
                  {...form.register("instructions")}
                />
              </>
            }
            sources={
              <SourceChecklist
                options={sourceOptions(sources)}
                onToggle={(value) =>
                  form.setValue(
                    "sources",
                    toggled(sources, value as DraftingSourceEnum),
                  )
                }
                extra={
                  sources.includes(DraftingSourceEnum.Supporting) && (
                    <SupportingDocumentSelect
                      documents={drafting.supportingDocuments}
                      value={supportingDocumentId}
                      onChange={(id) =>
                        form.setValue("supportingDocumentId", id)
                      }
                    />
                  )
                }
              />
            }
            footer={
              <SourcesFooter
                note={section?.sourceNote}
                more="Your notes are typed on each case."
                onTemplate={drafting.onTemplate}
              />
            }
            error={error && <ErrorLine>{error}</ErrorLine>}
            actions={
              <>
                <Button type="button" color="white" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="navy"
                  size="sm"
                  loading={section ? drafting.saving : drafting.adding}
                  disabled={name.trim() === ""}
                >
                  {section ? "Save" : "Add section"}
                </Button>
              </>
            }
          />
        </Form>
      </FormProvider>
    </FloatingPanel>
  );
};
